import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, useUser } from '@clerk/clerk-react';

import { createApiClient } from '../api.js';
import { useResolvedTenant } from '../hooks/useResolvedTenant.js';
import { SetupChecklistCard } from '../components/SetupChecklistCard.jsx';

/**
 * Home — jobs-to-be-done dashboard.
 *
 * Nobody opens a business app for fun.  When you land here the page
 * should answer one question: "What should I do next?".  We synth a
 * small set of *jobs* from the current state of the tenant — pending
 * help replies, stale tickets, missing project, pending team
 * requests, blank tour library — and rank them so the most-pressing
 * one is the first card.
 *
 * Below the jobs is a smaller "Tools" grid for the routine actions
 * that don't need a signal to surface (Audit log, SDK builds, …).
 * Below that, the live stat tiles so an operator can still tell at
 * a glance whether anything's moving.
 *
 * Visibility is role-aware.  Editor never sees "Approve access
 * request"; Viewer sees only read-only jobs.
 */

const ICONS = {
  reply: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
    </svg>
  ),
  follow: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
    </svg>
  ),
  approve: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <line x1="19" y1="8" x2="19" y2="14" />
      <line x1="22" y1="11" x2="16" y2="11" />
    </svg>
  ),
  project: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 7l9-4 9 4-9 4z" /><path d="M3 12l9 4 9-4" /><path d="M3 17l9 4 9-4" />
    </svg>
  ),
  tour: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  ),
  csat: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  ),
  team: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
      <circle cx="9" cy="7" r="4" />
    </svg>
  ),
  sdk: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" />
      <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
      <line x1="12" y1="22.08" x2="12" y2="12" />
    </svg>
  ),
  audit: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  ),
  spark: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2v4" /><path d="M12 18v4" /><path d="M4.93 4.93l2.83 2.83" />
      <path d="M16.24 16.24l2.83 2.83" /><path d="M2 12h4" /><path d="M18 12h4" />
    </svg>
  ),
};

const ROLE_RANK = { owner: 4, admin: 3, editor: 2, viewer: 1 };
function roleAtLeast(role, min) {
  return (ROLE_RANK[role] || 0) >= (ROLE_RANK[min] || 0);
}

export function Home() {
  const { user } = useUser();
  const { getToken } = useAuth();
  const api = useMemo(() => createApiClient(getToken), [getToken]);
  // Card 097 — don't fire the dashboard's data swarm until we have a
  // tenant the backend actually knows.  Pre-resolved tenant requests
  // 404'd in a wall and looked like an outage.
  const { isResolved } = useResolvedTenant();
  const firstName = user?.firstName || 'there';

  const [data, setData] = useState({
    role: null,
    projects: null,
    tours: null,
    members: null,
    audit24h: null,
    pendingAccess: null,
    helpOpen: null,
    helpStaleCount: null,
    helpAssignedToMe: null,
    helpStats: null,
  });

  useEffect(() => {
    if (!isResolved) return undefined;
    let cancelled = false;
    const fetchSafe = async (path) => {
      try { return await api(path); } catch { return null; }
    };
    (async () => {
      const [projects, tours, audit, pending, helpInbox, team, helpStats] = await Promise.all([
        fetchSafe('/v1/admin/projects'),
        fetchSafe('/v1/admin/tours?status=published'),
        fetchSafe('/v1/admin/audit?date_from=' + new Date(Date.now() - 86_400_000).toISOString().slice(0, 10)),
        fetchSafe('/v1/admin/access-requests?status_filter=pending'),
        fetchSafe('/v1/admin/help/requests?status=open'),
        fetchSafe('/v1/admin/team'),
        fetchSafe('/v1/admin/help/stats?days=14'),
      ]);
      if (cancelled) return;

      const teamList = Array.isArray(team?.members)
        ? team.members
        : (Array.isArray(team?.items) ? team.items : (Array.isArray(team) ? team : []));
      const self = teamList.find((m) => m.is_self);
      const role = self?.role || null;
      const myId = user?.id || '';

      const openItems = helpInbox?.items || [];
      const now = Date.now();
      const stale = openItems.filter((r) => {
        const created = r.created_at ? new Date(r.created_at).getTime() : now;
        return (now - created) >= 86_400_000; // 24h+
      }).length;
      const assignedToMe = openItems.filter((r) => r.assignee_id === myId).length;

      setData({
        role,
        projects: Array.isArray(projects?.items)
          ? projects.items.length
          : Array.isArray(projects) ? projects.length : null,
        tours: tours?.items?.length ?? null,
        members: teamList.length || null,
        audit24h: audit?.items?.length ?? null,
        pendingAccess: Array.isArray(pending?.items)
          ? pending.items.length
          : Array.isArray(pending) ? pending.length : null,
        helpOpen: openItems.length,
        helpStaleCount: stale,
        helpAssignedToMe: assignedToMe,
        helpStats: helpStats?.data || helpStats || null,
      });
    })();
    return () => { cancelled = true; };
  }, [api, user?.id, isResolved]);

  const jobs = useMemo(() => buildJobs(data), [data]);
  const tools = useMemo(() => buildTools(data.role), [data.role]);

  return (
    <div className="page">
      <div className="page__title">
        <div>
          <h1>Hi {firstName}.</h1>
          <p className="page__subtitle">
            {data.role
              ? `You're signed in as ${roleLabel(data.role)}. Here's what's worth your attention.`
              : `Here's what's worth your attention.`}
          </p>
        </div>
      </div>

      {/* Card 090 — tenant setup checklist sits at the top so an
          operator landing on Home sees outstanding wiring before
          anything else. Self-collapses once everything ticks, and
          stays hidden until the next tab if explicitly dismissed.
          Gated on isResolved so we don't fan out to /v1/admin/setup
          before the tenant is known to the backend. */}
      {isResolved && <SetupChecklistCard scope="tenant" />}

      {jobs.length > 0 ? (
        <section className="home-section">
          <div className="home-section__title">What needs you</div>
          <div className="home-jobs">
            {jobs.map((j) => <JobCard key={j.id} job={j} />)}
          </div>
        </section>
      ) : (
        <section className="home-section">
          <div className="home-empty">
            <span aria-hidden="true">{ICONS.spark}</span>
            <div>
              <div className="home-empty__title">You're caught up.</div>
              <div className="home-empty__body">
                No tickets need a reply, no pending invites, nothing stale.
                Take a breather, or build out something new from the tools below.
              </div>
            </div>
          </div>
        </section>
      )}

      <section className="home-section">
        <div className="home-section__title">Tools</div>
        <div className="home-tools">
          {tools.map((t) => <ToolCard key={t.to} tool={t} />)}
        </div>
      </section>

      <section className="home-section">
        <div className="home-section__title">At a glance · last 14 days</div>
        <Glance data={data} />
      </section>

      <style>{HOME_CSS}</style>
    </div>
  );
}

/* ── Job synthesis ───────────────────────────────────────────────────
 *
 * Each entry in the returned array is a card the user can act on
 * immediately.  Order matters — earlier = higher signal.  Each job
 * is gated by both data (count > 0) and role (don't suggest invites
 * to viewers).
 */
function buildJobs(d) {
  const out = [];
  const role = d.role || 'viewer';

  if (typeof d.helpAssignedToMe === 'number' && d.helpAssignedToMe > 0 && roleAtLeast(role, 'admin')) {
    out.push({
      id: 'help-mine',
      title: d.helpAssignedToMe === 1 ? '1 ticket assigned to you' : `${d.helpAssignedToMe} tickets assigned to you`,
      body: 'Your queue. Reply now and the user sees it within a few seconds.',
      cta: 'Open my queue',
      to: '/help',
      icon: ICONS.reply,
      tone: 'urgent',
    });
  }

  if (typeof d.helpStaleCount === 'number' && d.helpStaleCount > 0 && roleAtLeast(role, 'admin')) {
    out.push({
      id: 'help-stale',
      title: d.helpStaleCount === 1 ? '1 ticket has been open over a day' : `${d.helpStaleCount} tickets open over a day`,
      body: 'These users haven\'t heard back. A quick acknowledgement keeps trust intact.',
      cta: 'Triage stale tickets',
      to: '/help',
      icon: ICONS.follow,
      tone: 'warn',
    });
  }

  if (typeof d.helpOpen === 'number' && d.helpOpen > 0
      && !d.helpStaleCount && !d.helpAssignedToMe
      && roleAtLeast(role, 'admin')) {
    out.push({
      id: 'help-open',
      title: d.helpOpen === 1 ? '1 ticket waiting for a reply' : `${d.helpOpen} tickets waiting for a reply`,
      body: 'Nothing\'s stale yet. Take one before that changes.',
      cta: 'Open the inbox',
      to: '/help',
      icon: ICONS.reply,
      tone: 'normal',
    });
  }

  if (typeof d.pendingAccess === 'number' && d.pendingAccess > 0 && roleAtLeast(role, 'admin')) {
    out.push({
      id: 'access',
      title: d.pendingAccess === 1 ? '1 person wants admin access' : `${d.pendingAccess} people want admin access`,
      body: 'They hit the bookmarklet or asked from inside your product. Pick a role and approve, or deny with a note.',
      cta: 'Review requests',
      to: '/members',
      icon: ICONS.approve,
      tone: 'normal',
    });
  }

  // Card 104 — only nudge to create a project when we KNOW the count is
  // zero (post-fetch), not while loading (d.projects === null).  Before
  // this the nudge fired during the brief loading window and confused
  // operators whose tenant DOES have projects.
  if (d.projects === 0 && d.projects !== null && roleAtLeast(role, 'admin')) {
    out.push({
      id: 'first-project',
      title: 'Create your first project',
      body: 'A project scopes API keys, tours, content, and the help inbox. You\'ll mint a key for your dev site in a minute.',
      cta: 'Create a project',
      to: '/projects?create=1',
      icon: ICONS.project,
      tone: 'primary',
    });
  }

  if (typeof d.tours === 'number' && d.tours === 0
      && (d.projects || 0) > 0
      && roleAtLeast(role, 'editor')) {
    out.push({
      id: 'first-tour',
      title: 'Author your first tour',
      body: 'A tour walks new users through your product step-by-step. Five clicks in the bookmarklet ship one.',
      cta: 'Build a tour',
      to: '/projects',
      icon: ICONS.tour,
      tone: 'normal',
    });
  }

  // CSAT-driven nudge: if avg rating is below 4 over the window, point at it.
  const csat = d.helpStats?.csat_percent;
  if (typeof csat === 'number' && d.helpStats?.rated >= 3 && csat < 70
      && roleAtLeast(role, 'admin')) {
    out.push({
      id: 'csat-low',
      title: `CSAT is ${csat.toFixed(0)}% — under the 70% threshold`,
      body: `${d.helpStats.rated} users rated their experience over the last ${d.helpStats.window_days} days. Worth reading the low-rated threads before they churn.`,
      cta: 'Open the inbox',
      to: '/help',
      icon: ICONS.csat,
      tone: 'warn',
    });
  }

  return out;
}

/* ── Tools ────────────────────────────────────────────────────────── */

function buildTools(role) {
  const r = role || 'viewer';
  const all = [
    { to: '/help', title: 'Help inbox', body: 'Reply to users, triage by priority, promote answers to FAQ.', icon: ICONS.reply, accent: 'sky', min: 'admin' },
    { to: '/projects', title: 'Projects', body: 'Pick a project to author tours, content, themes, or mint a key.', icon: ICONS.project, accent: 'indigo', min: 'viewer' },
    { to: '/members', title: 'Team & access', body: 'Invite teammates, scope them to projects, manage roles.', icon: ICONS.team, accent: 'amber', min: 'admin' },
    { to: '/help/articles', title: 'Help articles', body: 'Curate the quick-answers users see in the widget.', icon: ICONS.csat, accent: 'emerald', min: 'editor' },
    { to: '/sdk-version', title: 'SDK builds', body: 'What\'s on the CDN, what `latest` points at.', icon: ICONS.sdk, accent: 'emerald', min: 'viewer' },
    { to: '/audit', title: 'Audit log', body: 'Every admin action, tamper-evident by SHA-256 chain.', icon: ICONS.audit, accent: 'rose', min: 'admin' },
  ];
  return all.filter((t) => roleAtLeast(r, t.min));
}

/* ── Glance strip ────────────────────────────────────────────────── */

function Glance({ data }) {
  const d = data;
  const stats = d.helpStats || {};
  return (
    <div className="home-glance">
      <GlanceTile label="Projects" value={d.projects} />
      <GlanceTile label="Tickets last 14d" value={stats.total} />
      <GlanceTile
        label="MTTR"
        value={stats.avg_resolution_seconds != null ? formatDuration(stats.avg_resolution_seconds) : null}
      />
      <GlanceTile
        label="CSAT"
        value={stats.csat_percent != null ? `${stats.csat_percent.toFixed(0)}%` : null}
      />
      <GlanceTile label="Published tours" value={d.tours} />
      <GlanceTile label="Audit events 24h" value={d.audit24h} />
    </div>
  );
}

function GlanceTile({ label, value }) {
  return (
    <div className="home-glance__tile">
      <div className="home-glance__value">{value == null ? '—' : value}</div>
      <div className="home-glance__label">{label}</div>
    </div>
  );
}

function formatDuration(secs) {
  if (secs == null) return '—';
  if (secs < 60) return `${Math.round(secs)}s`;
  const m = secs / 60;
  if (m < 60) return `${Math.round(m)}m`;
  const h = m / 60;
  if (h < 24) return `${h.toFixed(h < 10 ? 1 : 0)}h`;
  const d = h / 24;
  return `${d.toFixed(d < 10 ? 1 : 0)}d`;
}

/* ── Cards ───────────────────────────────────────────────────────── */

function JobCard({ job }) {
  return (
    <Link to={job.to} className={`home-job home-job--${job.tone}`}>
      <span className="home-job__icon" aria-hidden="true">{job.icon}</span>
      <div className="home-job__body">
        <div className="home-job__title">{job.title}</div>
        <div className="home-job__desc">{job.body}</div>
      </div>
      <span className="home-job__cta">{job.cta} →</span>
    </Link>
  );
}

function ToolCard({ tool }) {
  return (
    <Link to={tool.to} className={`home-tool home-tool--${tool.accent}`}>
      <span className="home-tool__icon" aria-hidden="true">{tool.icon}</span>
      <div className="home-tool__title">{tool.title}</div>
      <p className="home-tool__body">{tool.body}</p>
    </Link>
  );
}

function roleLabel(r) {
  return { owner: 'an owner', admin: 'an admin', editor: 'an editor', viewer: 'a viewer' }[r] || `a ${r}`;
}

const HOME_CSS = `
  .home-section { margin-bottom: 24px; }
  .home-section__title {
    font-size: 11.5px;
    font-weight: 700;
    color: var(--text-3, #64748b);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin: 0 0 10px;
  }

  .home-empty {
    display: flex;
    gap: 14px;
    align-items: center;
    padding: 20px 22px;
    background: var(--surface-0, #fff);
    border: 1px solid var(--border-1, #e5e7eb);
    border-radius: 12px;
    color: var(--text-2, #334155);
  }
  .home-empty span { color: var(--brand-600, #4f46e5); display: inline-flex; }
  .home-empty__title { font-weight: 600; color: var(--text-1, #0f172a); margin-bottom: 2px; }
  .home-empty__body  { font-size: 13.5px; color: var(--text-3, #64748b); }

  /* Jobs — one row each so the title + CTA can breathe and the order
     reads top-to-bottom as a checklist. */
  .home-jobs { display: flex; flex-direction: column; gap: 10px; }
  .home-job {
    display: grid;
    grid-template-columns: 44px 1fr auto;
    gap: 14px;
    align-items: center;
    padding: 14px 18px;
    background: var(--surface-0, #fff);
    border: 1px solid var(--border-1, #e5e7eb);
    border-left: 3px solid var(--accent, #94a3b8);
    border-radius: 12px;
    text-decoration: none;
    color: inherit;
    transition: border-color 120ms ease, box-shadow 120ms ease, transform 120ms ease;
  }
  .home-job:hover {
    transform: translateY(-1px);
    border-color: var(--accent, #94a3b8);
    box-shadow: 0 12px 28px -18px var(--accent, #94a3b8);
    text-decoration: none;
  }
  .home-job__icon {
    width: 44px; height: 44px;
    display: inline-flex; align-items: center; justify-content: center;
    border-radius: 10px;
    background: var(--accent-soft);
    color: var(--accent);
  }
  .home-job__title { font-size: 14.5px; font-weight: 700; color: var(--text-1, #0f172a); }
  .home-job__desc  { font-size: 12.5px; color: var(--text-3, #64748b); margin-top: 2px; line-height: 1.45; }
  .home-job__cta   {
    font-size: 12.5px; font-weight: 700;
    padding: 6px 10px; border-radius: 8px;
    background: var(--accent); color: #fff;
    white-space: nowrap;
  }
  .home-job--urgent  { --accent: #dc2626; --accent-soft: rgba(220,38,38,0.10); }
  .home-job--warn    { --accent: #ea580c; --accent-soft: rgba(234,88,12,0.10); }
  .home-job--primary { --accent: #4f46e5; --accent-soft: rgba(79,70,229,0.10); }
  .home-job--normal  { --accent: #0ea5e9; --accent-soft: rgba(14,165,233,0.10); }

  /* Tools — a quieter grid below.  Each tool is a card with an icon. */
  .home-tools {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 12px;
  }
  .home-tool {
    display: block;
    background: var(--surface-0, #fff);
    border: 1px solid var(--border-1, #e5e7eb);
    border-radius: 10px;
    padding: 14px;
    color: inherit;
    text-decoration: none;
    transition: border-color 120ms ease, transform 120ms ease;
  }
  .home-tool:hover {
    transform: translateY(-1px);
    border-color: var(--accent);
    text-decoration: none;
  }
  .home-tool__icon {
    display: inline-flex; align-items: center; justify-content: center;
    width: 32px; height: 32px;
    border-radius: 8px;
    background: var(--accent-soft);
    color: var(--accent);
    margin-bottom: 8px;
  }
  .home-tool__title { font-weight: 700; font-size: 13.5px; color: var(--text-1, #0f172a); margin-bottom: 2px; }
  .home-tool__body  { margin: 0; font-size: 12px; color: var(--text-3, #64748b); line-height: 1.45; }
  .home-tool--indigo  { --accent: #6366f1; --accent-soft: rgba(99,102,241,0.10); }
  .home-tool--emerald { --accent: #10b981; --accent-soft: rgba(16,185,129,0.12); }
  .home-tool--amber   { --accent: #f59e0b; --accent-soft: rgba(245,158,11,0.14); }
  .home-tool--rose    { --accent: #f43f5e; --accent-soft: rgba(244,63,94,0.12); }
  .home-tool--sky     { --accent: #0ea5e9; --accent-soft: rgba(14,165,233,0.12); }

  /* Glance — small numeric tiles. */
  .home-glance {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 10px;
  }
  .home-glance__tile {
    background: var(--surface-0, #fff);
    border: 1px solid var(--border-1, #e5e7eb);
    border-radius: 10px;
    padding: 10px 12px;
  }
  .home-glance__value { font-size: 18px; font-weight: 700; color: var(--text-1, #0f172a); font-variant-numeric: tabular-nums; line-height: 1.15; }
  .home-glance__label { font-size: 11px; color: var(--text-3, #64748b); text-transform: uppercase; letter-spacing: 0.04em; margin-top: 2px; }

  @media (prefers-reduced-motion: reduce) {
    .home-job, .home-tool { transition: none; }
  }
`;
